import {useState, useCallback, useEffect} from 'react'
import axios from 'axios'
import {API_ENDPOINTS} from '../constants/apiConstants'
import {ApiResponseModel, BillingDataModel} from '../entities/entities'
import {mapRawToBillingData, RawBillingItem} from '../utitlities/billingReport.mapper'
import { useReduxAuth } from '../modules/auth'
import { USER_ROLES } from '../constants/StatusConstants'

/**
 * @param initialMonthYear e.g. "May 2025"
 * @param doctorId optional: filters the report for a specific doctor
 */
export function useBillingReport(initialMonthYear: string, doctorId?: number) {
  const [data, setData] = useState<BillingDataModel[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { currentUser } = useReduxAuth()

  const fetchReport = useCallback(
    async (monthYear: string) => {
      setLoading(true)
      setError(null)
      try {

        const queryParams = new URLSearchParams()
        if ((currentUser.role_id === USER_ROLES.BILLING_PERSONNEL || currentUser.role_id === USER_ROLES.MEDICAL_ASSISTANT) && doctorId) {
          queryParams.append('doctor_id', doctorId.toString());
}

        const res = await axios.get<ApiResponseModel<{billingData: RawBillingItem[]}>>(
          API_ENDPOINTS.GET_ALL_BILLING_REPORT(queryParams.toString())
        )

        const shaped = res.data.data.billingData.map(mapRawToBillingData)
        setData(shaped)
      } catch (e: any) {
        const statusCode = e.response?.status;
        const msg = e.response?.data?.message || e.message;

        if (statusCode === 403) {
          setData([]);
          return;
        }
        // Only show actual errors (network, 500, etc.)
        setError(msg);
      } finally {
        setLoading(false)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [doctorId]
  )

  const toggleStatus = useCallback(async (row: BillingDataModel & {notes?: string}) => {
    const newStatus = row.Patient.status === 1 ? 0 : 1
    await axios.put(API_ENDPOINTS.UPDATE_PATIENT_STATUS(row.patient_id, newStatus), {
      notes: row.notes,
    })
    setData((prev) =>
      prev.map((r) =>
        r.patient_id === row.patient_id
          ? {
              ...r,
              Patient: {...r.Patient, status: newStatus},
              status: newStatus,
            }
          : r
      )
    )
  }, [])

  // whenever monthYear or doctorId changes, re-fetch
  useEffect(() => {
    // only fire when we actually have a doctorId
    if (doctorId == null) return
    fetchReport(initialMonthYear)
  }, [fetchReport, initialMonthYear, doctorId])

  return {data, loading, error, fetchReport, toggleStatus}
}
